import { HisWidgetBase } from './his-widget-base';
import { signal, WritableSignal } from '@angular/core';

export class HisOptionWidget extends HisWidgetBase<string> {
    kind = 'checkbox';

    // the value from the search config file the checkbox will have when it is marked (typically "1")
    option: string;

    /**
     * Internal state of the checkbox. Determines the checked position based on the externally set value and stores it as 
     * either true or false. Whether a value is considered as true is decided by {@link isCheckboxSelected}.
     * 
     * This property is to be considered as the `single source of truth`, as it is influenced both by the backing search data as well as the UI.
     */
    private checkboxValue: WritableSignal<boolean | undefined>;

    /**
     * Accessor for the checkbox's value as defined in the search configuration.
     * 
     * Setting this property updates the internal {@link checkboxValue} state accordingly. 
     * - When setting this property, the input is converted to a boolean state using {@link isCheckboxSelected}.
     * - When reading this property, it returns the {@link option} value if selected, or `null` if unselected.
     * 
     * The internal boolean state is maintained by {@link checkboxValue} as the single source of truth,
     * influenced by both backing search data and UI interactions.
     */
    get value(): string {
        return this.checkboxValue() ? this.option : null;
    }

    // Setter updates the internal checkbox state based on the provided value. TSDoc defined on the getter.
    set value( value: any ) {
        if ( !this.checkboxValue ) {
            //necessary, as the constructor implicitly calls this before 'checkboxValue' can be initialized at declaration
            this.checkboxValue = signal<boolean>( false );
        }

        this.checkboxValue.set( this.isCheckboxSelected( value ) );
    }

    /**
     * Exposed readonly state of {@link checkboxValue}. 
     */
    get checkboxValueChanged() {
        return this.checkboxValue.asReadonly();
    }

    constructor( options: any = {} ) {
        super( options );

        this.option = options.option || '?';
    }

    isWidgetModified(): boolean {
        let widgetValue = this.isCheckboxSelected( this.value );
        let widgetOriginalValue = this.isCheckboxSelected( this.originalValue );
        return widgetValue !== widgetOriginalValue;
    }

    /**
     * Prüft, ob eine Checkbox ausgewählt ist, sowohl für widget.value, als auch für widget.originalValue
     *
     * @param value
     * <p>Die Checkbox ist nicht ausgewählt falls value =</p>
     * <ul>
     * <li>undefined</li>
     * <li>null</li>
     * <li>false: boolean</li>
     * </ul>
     * <p>Die Checkbox ist ausgewählt falls value =</p>
     * <ul>
     * <li>true: boolean</li>
     * <li>irgendein Wert: string (das ist der Value aus der Suchkonfigurationsdatei)</li>
     * </ul>
     */
    private isCheckboxSelected( value: any ): boolean {
        if ( value === undefined || value === null ) {
            return false;
        }
        if ( typeof value === 'boolean' ) {
            if ( value === true ) {
                return true;
            } else {
                return false;
            }
        }
        if ( typeof value === 'string' ) {
            return true;
        }
        return false;
    }

}
